/* eslint-disable */
import React from "react";
import { supabase } from "./supabaseClient";

const C = {
  primary:"#1a6b3c", primaryPale:"#e8f5ee", primaryDark:"#0d3d1f",
  amber:"#d97706",   amberPale:"#fffbeb",
  rose:"#e11d48",    rosePale:"#fef2f2",
  blue:"#2563eb",    bluePale:"#eff6ff",
  purple:"#7c3aed",  purplePale:"#f5f3ff",
  text:"#111827",    muted:"#6b7280",  border:"#d1fae5",
  white:"#ffffff",   shadow:"rgba(0,0,0,0.06)",
};

const STATUS_META = {
  present:   { label:"Present",   col:"#16a34a", bg:"#f0fdf4", border:"#bbf7d0", icon:"✅" },
  absent:    { label:"Absent",    col:C.rose,    bg:C.rosePale,  border:"#fecaca", icon:"❌" },
  late:      { label:"Late",      col:C.amber,   bg:C.amberPale, border:"#fde68a", icon:"⏰" },
  half_day:  { label:"Half Day",  col:C.blue,    bg:C.bluePale,  border:"#bfdbfe", icon:"🌗" },
  on_leave:  { label:"On Leave",  col:C.purple,  bg:C.purplePale,border:"#ddd6fe", icon:"🏖️" },
};

const STATUSES = Object.keys(STATUS_META);

const todayStr = () => new Date().toISOString().split("T")[0];
const fmtDate  = d => d ? new Date(d+"T00:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"}) : "—";
const fmtMonth = d => d ? new Date(d+"-01").toLocaleDateString("en-GB",{month:"long",year:"numeric"}) : "";

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div style={{position:"fixed",top:20,right:20,zIndex:9999,background:toast.ok?"#f0fdf4":"#fef2f2",color:toast.ok?"#16a34a":"#dc2626",border:`1px solid ${toast.ok?"#bbf7d0":"#fecaca"}`,borderRadius:10,padding:"10px 18px",fontWeight:700,fontSize:13,boxShadow:"0 4px 18px rgba(0,0,0,.15)"}}>
      {toast.msg}
    </div>
  );
}

export function StaffAttendanceAdmin() {
  const [staffList,   setStaffList]   = React.useState([]);
  const [view,        setView]        = React.useState("register");
  const [date,        setDate]        = React.useState(todayStr());
  const [register,    setRegister]    = React.useState({});
  const [saved,       setSaved]       = React.useState({});
  const [saving,      setSaving]      = React.useState(false);
  const [loading,     setLoading]     = React.useState(true);
  const [toast,       setToast]       = React.useState(null);
  const [histMonth,   setHistMonth]   = React.useState(todayStr().slice(0,7));
  const [histRecords, setHistRecords] = React.useState([]);
  const [histLoading, setHistLoading] = React.useState(false);
  const [filterStaff, setFilterStaff] = React.useState("all");

  const showToast = (msg,ok=true)=>{setToast({msg,ok});setTimeout(()=>setToast(null),3500);};

  React.useEffect(()=>{
    supabase.from("staff").select("id,name,role,status").eq("status","Active").order("name")
      .then(({data})=>{ setStaffList(data||[]); });
  },[]);

  React.useEffect(()=>{
    if (!staffList.length) return;
    (async()=>{
      setLoading(true);
      const {data} = await supabase.from("staff_attendance").select("*").eq("date",date);
      const existing = {};
      (data||[]).forEach(r=>{ existing[r.staff_id] = r; });
      setSaved(existing);
      const init = {};
      staffList.forEach(s=>{
        if (existing[s.id]) {
          init[s.id] = { status:existing[s.id].status, time_in:existing[s.id].time_in||"", time_out:existing[s.id].time_out||"", notes:existing[s.id].notes||"" };
        } else {
          init[s.id] = { status:"present", time_in:"", time_out:"", notes:"" };
        }
      });
      setRegister(init);
      setLoading(false);
    })();
  },[date, staffList]);

  React.useEffect(()=>{
    if (view!=="history") return;
    setHistLoading(true);
    const from = histMonth+"-01";
    const to   = histMonth+"-31";
    supabase.from("staff_attendance").select("*").gte("date",from).lte("date",to).order("date",{ascending:false})
      .then(({data})=>{ setHistRecords(data||[]); setHistLoading(false); });
  },[view, histMonth]);

  const setEntry = (staffId, key, val) => setRegister(p=>({...p,[staffId]:{...p[staffId],[key]:val}}));

  const markAll = (status) => {
    setRegister(p=>{
      const next = {...p};
      staffList.forEach(s=>{ next[s.id] = {...next[s.id], status}; });
      return next;
    });
  };

  const saveRegister = async () => {
    setSaving(true);
    const rows = staffList.map(s=>({
      staff_id:   s.id,
      staff_name: s.name,
      date,
      status:     register[s.id]?.status || "present",
      time_in:    register[s.id]?.time_in  || null,
      time_out:   register[s.id]?.time_out || null,
      notes:      register[s.id]?.notes    || null,
    }));
    const {error} = await supabase.from("staff_attendance").upsert(rows, {onConflict:"staff_id,date"});
    if (error) { showToast("Error: "+error.message, false); setSaving(false); return; }
    const newSaved = {};
    rows.forEach(r=>{ newSaved[r.staff_id] = r; });
    setSaved(newSaved);
    showToast("Attendance saved for "+fmtDate(date)+" ✅");
    setSaving(false);
  };

  const dateSummary = React.useMemo(()=>{
    const counts = {};
    STATUSES.forEach(s=>{ counts[s]=0; });
    staffList.forEach(s=>{ const st=register[s.id]?.status||"present"; counts[st]=(counts[st]||0)+1; });
    return counts;
  },[register, staffList]);

  const staffSummary = React.useMemo(()=>{
    if (!histRecords.length) return [];
    const byStaff = {};
    histRecords.forEach(r=>{
      if (!byStaff[r.staff_name]) byStaff[r.staff_name]={present:0,absent:0,late:0,half_day:0,on_leave:0,total:0};
      byStaff[r.staff_name][r.status]=(byStaff[r.staff_name][r.status]||0)+1;
      byStaff[r.staff_name].total++;
    });
    return Object.entries(byStaff).map(([name,counts])=>({
      name, ...counts,
      pct: Math.round((counts.present + counts.late*0.5 + counts.half_day*0.5) / counts.total * 100),
    })).sort((a,b)=>a.pct-b.pct);
  },[histRecords]);

  const histStaff  = [...new Set(histRecords.map(r=>r.staff_name))].sort();
  const filteredHistRecords = filterStaff==="all" ? histRecords : histRecords.filter(r=>r.staff_name===filterStaff);
  const groupedByDate = {};
  filteredHistRecords.forEach(r=>{ (groupedByDate[r.date]||=[]).push(r); });

  const inp = {padding:"6px 8px",borderRadius:6,border:"1px solid #e5e7eb",fontSize:11,boxSizing:"border-box"};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <Toast toast={toast}/>

      <div style={{background:C.white,borderRadius:14,padding:"18px 22px",border:`1px solid ${C.border}`,boxShadow:`0 2px 10px ${C.shadow}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12,marginBottom:14}}>
          <div>
            <div style={{fontWeight:900,fontSize:17,color:C.primary}}>🧑‍💼 Staff Attendance Register</div>
            <div style={{fontSize:12,color:C.muted,marginTop:2}}>{staffList.length} active staff members</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            {["register","history"].map(v=>(
              <button key={v} onClick={()=>setView(v)}
                style={{background:view===v?C.primary:"#f3f4f6",color:view===v?"white":"#374151",border:"none",borderRadius:9,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                {v==="register"?"📋 Daily Register":"📅 History & Stats"}
              </button>
            ))}
          </div>
        </div>

        {view==="register"&&(
          <>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
              <input type="date" value={date} onChange={e=>setDate(e.target.value)}
                style={{padding:"8px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,fontWeight:600}}/>
              <span style={{fontSize:13,color:C.muted,fontWeight:600}}>{fmtDate(date)}</span>
              {Object.keys(saved).length>0&&<span style={{fontSize:11,color:C.primary,fontWeight:700,background:C.primaryPale,borderRadius:6,padding:"2px 8px"}}>✅ Saved</span>}
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {STATUSES.map(s=>{const m=STATUS_META[s];return(
                <div key={s} style={{background:m.bg,border:`1px solid ${m.border}`,borderRadius:9,padding:"6px 12px",display:"flex",alignItems:"center",gap:6}}>
                  <span>{m.icon}</span>
                  <span style={{fontWeight:900,fontSize:16,color:m.col,lineHeight:1}}>{dateSummary[s]||0}</span>
                  <span style={{fontSize:10,color:C.muted,fontWeight:600}}>{m.label}</span>
                </div>
              );})}
            </div>
          </>
        )}
      </div>

      {view==="register"&&(
        <>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <span style={{fontSize:12,color:C.muted,fontWeight:700}}>Mark all:</span>
            {STATUSES.map(s=>{const m=STATUS_META[s];return(
              <button key={s} onClick={()=>markAll(s)}
                style={{background:m.bg,color:m.col,border:`1px solid ${m.border}`,borderRadius:7,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                {m.icon} {m.label}
              </button>
            );})}
          </div>

          {loading ? <div style={{color:C.muted,fontSize:13,padding:16}}>Loading…</div> : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {staffList.map(s=>{
                const entry = register[s.id]||{status:"present",time_in:"",time_out:"",notes:""};
                const sm = STATUS_META[entry.status]||STATUS_META.present;
                return (
                  <div key={s.id} style={{background:C.white,borderRadius:12,border:`1.5px solid ${sm.border}`,padding:"12px 16px",display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                    <div style={{minWidth:160,flex:1}}>
                      <div style={{fontWeight:800,fontSize:13,color:C.text}}>{s.name}</div>
                      <div style={{fontSize:11,color:C.muted}}>{s.role||"Staff"}</div>
                    </div>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      {STATUSES.map(st=>{
                        const m=STATUS_META[st];
                        const active=entry.status===st;
                        return(
                          <button key={st} onClick={()=>setEntry(s.id,"status",st)}
                            style={{background:active?m.bg:"white",color:active?m.col:"#9ca3af",border:`1.5px solid ${active?m.col:"#e5e7eb"}`,borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",transition:"all .1s"}}>
                            {m.icon} {m.label}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                      <div style={{display:"flex",alignItems:"center",gap:4}}>
                        <span style={{fontSize:10,color:C.muted,fontWeight:600}}>In</span>
                        <input type="time" value={entry.time_in} onChange={e=>setEntry(s.id,"time_in",e.target.value)} style={inp}/>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:4}}>
                        <span style={{fontSize:10,color:C.muted,fontWeight:600}}>Out</span>
                        <input type="time" value={entry.time_out} onChange={e=>setEntry(s.id,"time_out",e.target.value)} style={inp}/>
                      </div>
                      <input placeholder="Note…" value={entry.notes} onChange={e=>setEntry(s.id,"notes",e.target.value)} style={{...inp,minWidth:120}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{display:"flex",justifyContent:"flex-end",paddingBottom:20}}>
            <button onClick={saveRegister} disabled={saving||loading}
              style={{background:saving?"#9ca3af":`linear-gradient(135deg,${C.primary},#0d9488)`,color:"white",border:"none",borderRadius:10,padding:"11px 28px",fontWeight:800,fontSize:14,cursor:saving?"not-allowed":"pointer"}}>
              {saving?"Saving…":"💾 Save Register"}
            </button>
          </div>
        </>
      )}

      {view==="history"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
            <input type="month" value={histMonth} onChange={e=>setHistMonth(e.target.value)}
              style={{padding:"8px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,fontWeight:600}}/>
            <span style={{fontSize:13,color:C.muted}}>{fmtMonth(histMonth)}</span>
            <select value={filterStaff} onChange={e=>setFilterStaff(e.target.value)} style={{padding:"8px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:12}}>
              <option value="all">All Staff</option>
              {histStaff.map(n=><option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          {filterStaff==="all"&&staffSummary.length>0&&(
            <div style={{background:C.white,borderRadius:14,padding:"16px 20px",border:`1px solid ${C.border}`}}>
              <div style={{fontWeight:800,fontSize:13,color:C.text,marginBottom:12}}>Attendance Summary — {fmtMonth(histMonth)}</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {staffSummary.map(s=>{
                  const col = s.pct>=90?C.primary : s.pct>=75?C.amber : C.rose;
                  return(
                    <div key={s.name}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                        <span style={{fontWeight:700,color:C.text}}>{s.name}</span>
                        <span style={{color:col,fontWeight:700}}>{s.pct}% · {s.present}P{s.absent>0?` ${s.absent}A`:""}{s.late>0?` ${s.late}L`:""}{s.half_day>0?` ${s.half_day}HD`:""}{s.on_leave>0?` ${s.on_leave}OL`:""}</span>
                      </div>
                      <div style={{background:"#f3f4f6",borderRadius:6,height:7,overflow:"hidden"}}>
                        <div style={{width:`${s.pct}%`,height:"100%",background:col,borderRadius:6,transition:"width .3s"}}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {histLoading ? <div style={{color:C.muted,fontSize:13,padding:16}}>Loading…</div> : (
            Object.keys(groupedByDate).length===0
              ? <div style={{background:C.white,borderRadius:12,padding:30,textAlign:"center",color:C.muted,fontSize:13,border:`1px solid ${C.border}`}}>No records for this period.</div>
              : Object.keys(groupedByDate).sort().reverse().map(d=>(
                <div key={d} style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
                  <div style={{background:"#f9fafb",padding:"10px 16px",fontWeight:800,fontSize:12,color:C.text,borderBottom:`1px solid ${C.border}`}}>{fmtDate(d)}</div>
                  <div style={{padding:"8px 16px",display:"flex",flexDirection:"column",gap:6}}>
                    {groupedByDate[d].sort((a,b)=>a.staff_name.localeCompare(b.staff_name)).map(r=>{
                      const sm=STATUS_META[r.status]||STATUS_META.present;
                      return(
                        <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,flexWrap:"wrap",gap:6}}>
                          <span style={{fontWeight:700,color:C.text}}>{r.staff_name}</span>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <span style={{background:sm.bg,color:sm.col,border:`1px solid ${sm.border}`,borderRadius:5,padding:"1px 7px",fontSize:11,fontWeight:700}}>{sm.icon} {sm.label}</span>
                            {r.time_in&&<span style={{color:C.muted}}>In: {r.time_in}</span>}
                            {r.time_out&&<span style={{color:C.muted}}>Out: {r.time_out}</span>}
                            {r.notes&&<span style={{color:C.muted,fontStyle:"italic"}}>{r.notes}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
          )}
        </div>
      )}
    </div>
  );
}
